import { readInventoryField } from '../compile/inventory-raw-readers';
import { surfaceField } from '../compile/from-base-encounter-media';
import { getStatusEffectDurationTimes } from '../ability/statuses/get-status-effect-duration-times';
import { activityStatusBefore, activityWrite, emptyActivity, statusScalar, type ActivityPlan } from '../actions/activity';
import { statusRows } from '../statuses/expiry';
import type { Row } from '../ability/context';
import type { inventoryDurationReaders } from './birth-inputs-20';
import { BURNABLE_LIGHT_IDS } from './pickup-light-ids';
export { BURNABLE_LIGHT_IDS } from './pickup-light-ids';

const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const upper = (value: unknown): string => String(value ?? '').toUpperCase();
const units = ['year', 'month', 'day', 'hour', 'minute', 'second'] as const;
const turnFlags = ['status_ends_on_target_start_of_turn', 'status_ends_on_target_end_of_turn',
    'status_ends_on_caster_start_of_next_turn', 'status_ends_on_caster_end_of_next_turn'] as const;

export interface PickupLightInput { source: Row; actor: Row; quantity: number; sourceType: string; moveType: string; merged: boolean }
/** Permanently lit mundane objects start one shared burn clock on a full, unmerged environment pickup. */
export function planPickupLight(input: PickupLightInput, read: {
    duration: ReturnType<typeof inventoryDurationReaders>; definition(id: string): Row | null; die(sides: number): number;
}): ActivityPlan {
    const plan = emptyActivity();
    if (!['encounter', 'location'].includes(input.sourceType) || input.merged
        || !['move_inventory', 'equip_inventory', 'finish_donning', 'give_inventory'].includes(input.moveType)
        || input.quantity < Number(readInventoryField(input.source, 'item_qty') ?? 1)) return plan;
    const actorId = String(surfaceField(record(input.actor.encounter_creatures) ?? input.actor, 'encounter_creature_id') ?? '');
    if (!actorId) return plan;
    const due = statusRows(input.source, 'target').filter(status => {
        const id = upper(statusScalar(status, 'status_item_id'));
        if (Number(statusScalar(status, 'status_deleted') ?? 0) || !BURNABLE_LIGHT_IDS.has(id)
            || units.some(unit => Number(statusScalar(status, `status_ends_campaign_${unit}`) ?? 0))) return false;
        const inline = record(status.status_arr);
        const definition = inline && (record(inline.item_layer_types) || record(inline.layers)) ? inline : read.definition(id);
        if (!definition) throw new Error(`inventory-pickup:light-definition:${id}`);
        return !read.duration.value(definition, 'magical_light') && read.duration.value(definition, 'duration_type') !== 'permanent';
    });
    if (!due.length || !read.duration.campaignClock()) return plan;
    const face = read.die(12), minutes = face * 10;
    const ends = getStatusEffectDurationTimes({ read: read.duration }, { item_layer_types: { duration_int: minutes, duration_type: 'minutes' } }, input.actor);
    const at = ends.ends.split('-').map(Number);
    for (const status of due) {
        const after: Row = Object.fromEntries(units.map((unit, i) => [`status_ends_campaign_${unit}`, at[i] ?? 0]));
        turnFlags.forEach((key, i) => { after[key] = ends.flags[i] ?? 0; });
        activityWrite(plan, { table: 'statuses', id: upper(statusScalar(status, 'status_id')),
            before: { ...activityStatusBefore(status, after), status_deleted: Number(statusScalar(status, 'status_deleted') ?? 0),
                status_target_id: String(statusScalar(status, 'status_target_id') ?? ''),
                status_connected_id: String(statusScalar(status, 'status_connected_id') ?? '') }, after });
    }
    plan.rows.push({ roll_type_1: 'light_source_kindle', roll_type_2: '', roll_item_id: String(readInventoryField(input.source, 'inventory_id')),
        roll_by: actorId, roll_to: actorId, roll_private: 0, roll_result: minutes, rolls_full: String(face),
        dice: { dice: 1, die: 12, mod: 0, adv_dis_stacks: 0, dc: 0 } });
    return plan;
}
